import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useVenues } from '../contexts/VenueContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiPlus, FiTrash2, FiUsers, FiHome, FiCheck } = FiIcons;

const AddVenueModal = ({ onClose }) => {
  const { addVenue } = useVenues();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'apartment',
    address: '',
    coordinates: { lat: '', lng: '' },
    photos: [''],
    facilities: [''],
    maxCapacity: 2, // Default capacity
    recommendedCapacity: 2, // Default recommended
    pricePerNight: '', // Used for per_unit
    pricingModel: 'per_unit', // 'per_unit' or 'occupancy_based'
    occupancyRates: [ // Used for occupancy_based
      { guests: 1, price: '' },
      { guests: 2, price: '' }
    ],
    priceIncludesUtilities: false,
    utilities: [
      {
        id: 'electricity',
        name: 'Electricity',
        priceType: 'per_day',
        billingBasis: 'per_unit',
        sameAllYear: true,
        periods: [{ startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: '' }]
      },
      {
        id: 'gas',
        name: 'Gas',
        priceType: 'per_day',
        billingBasis: 'per_unit',
        sameAllYear: true,
        periods: [{ startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: '' }]
      },
      {
        id: 'cleaning',
        name: 'Cleaning',
        priceType: 'per_booking',
        billingBasis: 'per_unit',
        sameAllYear: true,
        periods: [{ startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: '' }]
      },
      {
        id: 'bedsheets',
        name: 'Bed Sheets',
        priceType: 'per_booking',
        billingBasis: 'per_person',
        sameAllYear: true,
        periods: [{ startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: '' }]
      }
    ],
    selectedUtilities: []
  });

  // Handle Max Capacity Change to generate pricing rows
  const handleCapacityChange = (val) => {
    const newCap = parseInt(val) || 1;
    // Don't allow less than 1
    const safeCap = Math.max(1, newCap);

    setFormData(prev => {
      let newRates = [...prev.occupancyRates];
      
      if (safeCap > newRates.length) {
        // Add new rows
        for(let i = newRates.length + 1; i <= safeCap; i++) {
           newRates.push({ guests: i, price: '' });
        }
      } else if (safeCap < newRates.length) {
        // Remove rows
        newRates = newRates.slice(0, safeCap);
      }
      
      // Ensure recommended capacity doesn't exceed new max
      const newRecommended = Math.min(prev.recommendedCapacity, safeCap);

      return { 
        ...prev, 
        maxCapacity: safeCap, 
        recommendedCapacity: newRecommended,
        occupancyRates: newRates 
      };
    });
  };

  const handleOccupancyPriceChange = (guestCount, price) => {
    setFormData(prev => ({
      ...prev,
      occupancyRates: prev.occupancyRates.map(rate => 
        rate.guests === guestCount ? { ...rate, price: price } : rate
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const venueData = {
        ...formData,
        location: {
          address: formData.address,
          coordinates: {
            lat: parseFloat(formData.coordinates.lat) || 0,
            lng: parseFloat(formData.coordinates.lng) || 0
          }
        },
        photos: formData.photos.filter(photo => photo.trim()),
        facilities: formData.facilities.filter(facility => facility.trim()),
        pricePerNight: parseFloat(formData.pricePerNight) || 0,
        maxCapacity: parseInt(formData.maxCapacity) || 1,
        recommendedCapacity: parseInt(formData.recommendedCapacity) || 1,
        occupancyRates: formData.occupancyRates.map(r => ({ ...r, price: parseFloat(r.price) || 0 })),
        utilities: formData.utilities.map(utility => ({
          ...utility,
          periods: utility.periods.map(period => ({
            ...period,
            costPerDay: parseFloat(period.costPerDay) || 0
          }))
        }))
      };
      await addVenue(venueData);
      onClose();
    } catch (error) {
      console.error('Error adding venue:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Utility handlers...
  const handleUtilityChange = (utilityIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      utilities: prev.utilities.map((utility, i) => 
        i === utilityIndex ? { ...utility, [field]: value } : utility
      )
    }));
  };

  const addUtilityPeriod = (utilityIndex) => {
    setFormData(prev => ({
      ...prev,
      utilities: prev.utilities.map((utility, i) => 
        i === utilityIndex 
          ? { ...utility, periods: [...utility.periods, { startDate: '', endDate: '', costPerDay: '' }] }
          : utility
      )
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Add New Venue</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SafeIcon icon={FiX} className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Basic Information */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Venue Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter venue name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="apartment">Apartment</option>
                  <option value="house">House</option>
                  <option value="villa">Villa</option>
                  <option value="studio">Studio</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <input
                type="text"
                required
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter full address"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Capacity (Total Persons)
                </label>
                <input
                  type="number"
                  min="1"
                  required
                  value={formData.maxCapacity}
                  onChange={(e) => handleCapacityChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recommended Capacity (Beds)
                </label>
                <input
                  type="number"
                  min="1"
                  max={formData.maxCapacity}
                  required
                  value={formData.recommendedCapacity}
                  onChange={(e) => handleInputChange('recommendedCapacity', parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Number of people in standard beds.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.coordinates.lat}
                  onChange={(e) => handleInputChange('coordinates', { ...formData.coordinates, lat: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="40.7128"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.coordinates.lng}
                  onChange={(e) => handleInputChange('coordinates', { ...formData.coordinates, lng: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="-74.0060"
                />
              </div>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
              <label className="block text-sm font-bold text-gray-900 mb-4">
                Pricing Strategy
              </label>
              <div className="flex gap-6 mb-6">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="price-model-unit"
                    name="pricingModel"
                    checked={formData.pricingModel === 'per_unit'}
                    onChange={() => handleInputChange('pricingModel', 'per_unit')}
                    className="border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="price-model-unit" className="ml-2 text-sm text-gray-700 flex items-center">
                    <SafeIcon icon={FiHome} className="mr-1.5 h-4 w-4 text-gray-500" />
                    Flat Rate (Per Venue)
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="price-model-occupancy"
                    name="pricingModel"
                    checked={formData.pricingModel === 'occupancy_based'}
                    onChange={() => handleInputChange('pricingModel', 'occupancy_based')}
                    className="border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="price-model-occupancy" className="ml-2 text-sm text-gray-700 flex items-center">
                    <SafeIcon icon={FiUsers} className="mr-1.5 h-4 w-4 text-gray-500" />
                    Occupancy Based (Per Person Count)
                  </label>
                </div>
              </div>

              {formData.pricingModel === 'per_unit' ? (
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price per Night (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricePerNight}
                    onChange={(e) => handleInputChange('pricePerNight', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    placeholder="150.00"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-2">Set specific prices per night based on the number of guests:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {formData.occupancyRates.map((rate, index) => (
                      <div key={index} className="flex items-center bg-white p-2 rounded border border-gray-200">
                         <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-sm mr-3">
                           {rate.guests}
                         </div>
                         <div className="flex-1">
                           <label className="block text-xs text-gray-500">
                             {rate.guests} Person{rate.guests > 1 ? 's' : ''}
                           </label>
                           <div className="relative mt-1">
                             <span className="absolute left-2 top-1.5 text-gray-400">€</span>
                             <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                value={rate.price}
                                onChange={(e) => handleOccupancyPriceChange(rate.guests, e.target.value)}
                                className="w-full pl-6 pr-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                             />
                           </div>
                         </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-blue-600 flex items-center mt-2">
                    <SafeIcon icon={FiCheck} className="mr-1 h-3 w-3" />
                    Adjust "Max Capacity" above to add more rows.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Photos */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Photos</h3>
            {formData.photos.map((photo, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="url"
                  value={photo}
                  onChange={(e) => handleArrayChange('photos', index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com/photo.jpg"
                />
                {formData.photos.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeArrayItem('photos', index)}
                    className="text-red-600 hover:text-red-700 p-2"
                  >
                    <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addArrayItem('photos')}
              className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center"
            >
              <SafeIcon icon={FiPlus} className="mr-1 h-4 w-4" />
              Add Photo
            </button>
          </div>

          {/* Facilities */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Facilities</h3>
            {formData.facilities.map((facility, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={facility}
                  onChange={(e) => handleArrayChange('facilities', index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="WiFi, Kitchen, etc."
                />
                {formData.facilities.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeArrayItem('facilities', index)}
                    className="text-red-600 hover:text-red-700 p-2"
                  >
                    <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addArrayItem('facilities')}
              className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center"
            >
              <SafeIcon icon={FiPlus} className="mr-1 h-4 w-4" />
              Add Facility
            </button>
          </div>

          {/* Utilities */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Utilities & Pricing</h3>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="priceIncludesUtilities"
                checked={formData.priceIncludesUtilities}
                onChange={(e) => handleInputChange('priceIncludesUtilities', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="priceIncludesUtilities" className="ml-2 text-sm text-gray-700">
                Price includes all utilities
              </label>
            </div>

            {!formData.priceIncludesUtilities && (
              <div className="space-y-6">
                <p className="text-sm text-gray-600">
                  Configure utility costs that will be added to the base price (in Euro):
                </p>
                
                {formData.utilities.map((utility, utilityIndex) => (
                  <div key={utility.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900">{utility.name}</h4>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.selectedUtilities.includes(utility.id)}
                          onChange={(e) => {
                            const selected = e.target.checked;
                            handleInputChange('selectedUtilities', 
                              selected 
                                ? [...formData.selectedUtilities, utility.id]
                                : formData.selectedUtilities.filter(id => id !== utility.id)
                            );
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label className="ml-2 text-sm text-gray-700">Include in pricing</label>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Pricing Configuration Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-3 rounded-lg">
                        {/* Frequency */}
                        <div>
                          <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Frequency</span>
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id={`add-priceType-day-${utility.id}`}
                                name={`add-priceType-${utility.id}`}
                                checked={utility.priceType !== 'per_booking'}
                                onChange={() => handleUtilityChange(utilityIndex, 'priceType', 'per_day')}
                                className="border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <label htmlFor={`add-priceType-day-${utility.id}`} className="ml-2 text-sm text-gray-700">Per Day</label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id={`add-priceType-booking-${utility.id}`}
                                name={`add-priceType-${utility.id}`}
                                checked={utility.priceType === 'per_booking'}
                                onChange={() => handleUtilityChange(utilityIndex, 'priceType', 'per_booking')}
                                className="border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <label htmlFor={`add-priceType-booking-${utility.id}`} className="ml-2 text-sm text-gray-700">Per Booking (One-time)</label>
                            </div>
                          </div>
                        </div>

                        {/* Calculation Basis */}
                        <div>
                          <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Calculation Basis</span>
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id={`add-billingBasis-unit-${utility.id}`}
                                name={`add-billingBasis-${utility.id}`}
                                checked={utility.billingBasis !== 'per_person'}
                                onChange={() => handleUtilityChange(utilityIndex, 'billingBasis', 'per_unit')}
                                className="border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <label htmlFor={`add-billingBasis-unit-${utility.id}`} className="ml-2 text-sm text-gray-700">Per Venue (Flat)</label>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id={`add-billingBasis-person-${utility.id}`}
                                name={`add-billingBasis-${utility.id}`}
                                checked={utility.billingBasis === 'per_person'}
                                onChange={() => handleUtilityChange(utilityIndex, 'billingBasis', 'per_person')}
                                className="border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <label htmlFor={`add-billingBasis-person-${utility.id}`} className="ml-2 text-sm text-gray-700">Per Person (× Guests)</label>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Periods Toggle */}
                      <div className="flex gap-4 border-b border-gray-100 pb-2">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name={`utility-${utility.id}`}
                            checked={utility.sameAllYear}
                            onChange={() => handleUtilityChange(utilityIndex, 'sameAllYear', true)}
                            className="border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label className="ml-2 text-sm text-gray-700">Same cost all year</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name={`utility-${utility.id}`}
                            checked={!utility.sameAllYear}
                            onChange={() => handleUtilityChange(utilityIndex, 'sameAllYear', false)}
                            className="border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label className="ml-2 text-sm text-gray-700">Different periods</label>
                        </div>
                      </div>
                      
                      {utility.sameAllYear ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                             Cost (€)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={utility.periods[0]?.costPerDay || ''}
                            onChange={(e) => {
                              const updatedUtility = {
                                ...utility,
                                periods: [{ startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: e.target.value }]
                              };
                              handleUtilityChange(utilityIndex, 'periods', updatedUtility.periods);
                            }}
                            className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                            placeholder="5.00"
                          />
                           <p className="text-xs text-gray-500 mt-1">
                            {utility.billingBasis === 'per_person' ? 'Per person ' : 'Flat fee '} 
                            {utility.priceType === 'per_booking' ? 'per stay' : 'per day'}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {utility.periods.map((period, periodIndex) => (
                            <div key={periodIndex} className="grid grid-cols-3 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Start Date
                                </label>
                                <input
                                  type="date"
                                  value={period.startDate}
                                  onChange={(e) => {
                                    const updatedPeriods = [...utility.periods];
                                    updatedPeriods[periodIndex] = { ...period, startDate: e.target.value };
                                    handleUtilityChange(utilityIndex, 'periods', updatedPeriods);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  End Date
                                </label>
                                <input
                                  type="date"
                                  value={period.endDate}
                                  onChange={(e) => {
                                    const updatedPeriods = [...utility.periods];
                                    updatedPeriods[periodIndex] = { ...period, endDate: e.target.value };
                                    handleUtilityChange(utilityIndex, 'periods', updatedPeriods);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Cost (€)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={period.costPerDay}
                                  onChange={(e) => {
                                    const updatedPeriods = [...utility.periods];
                                    updatedPeriods[periodIndex] = { ...period, costPerDay: e.target.value };
                                    handleUtilityChange(utilityIndex, 'periods', updatedPeriods);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="5.00"
                                />
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addUtilityPeriod(utilityIndex)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-flex items-center"
                          >
                            <SafeIcon icon={FiPlus} className="mr-1 h-3 w-3" />
                            Add Period
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-semibold transition-colors"
            >
              {loading ? 'Creating...' : 'Create Venue'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AddVenueModal;